import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './MainPage.css';
import TabBar from '../../components/TabBar';
import BrowserNavBackButton from '../../components/BrowserNavBackButton/BrowserNavBackButton';
import BrowserCatalogShareButton from '../../components/BrowserCatalogShareButton/BrowserCatalogShareButton';
import { CatalogShareDispatchContext } from '../../contexts/CatalogShareContext';
import { useTabBarVisibility } from '../../contexts/TabBarVisibilityContext';
import { CatalogProvider } from '../../contexts/CatalogContext';
import { FeedProvider } from '../../contexts/FeedContext';
import { FittingDraftProvider } from '../../contexts/FittingDraftContext';
import { isTelegramWebAppEnvironment, hasTelegramWebAppInitData } from '../../utils/telegramEnvironment';
import { BrowserBackHandlerRefProvider, useBrowserBackHandlerRef } from '../../contexts/BrowserBackHandlerRefContext';
import { useAuth } from '../../contexts/AuthContext';
import { track } from '../../utils/productAnalytics';

const logoUrl = process.env.PUBLIC_URL + '/static/mainstatic/logo.svg';

/** Сегменты путей, на которых показываем таббар (только корневые вкладки). */
const ROOT_TAB_SEGMENTS = ['feed', 'cart', 'fitting', 'catalog', 'profile'];

/** HashRouter: иногда путь только в hash — как в resize-обработчике таббара. */
function routePathForBrowserUi(loc) {
  let p = (loc.pathname || '').trim();
  if (p && p !== '/' && p.includes('main')) {
    return p.replace(/\/+$/, '') || '/';
  }
  const h = loc.hash || '';
  if (h.startsWith('#')) {
    return (h.slice(1).split('?')[0] || '/').replace(/\/+$/, '') || '/';
  }
  return p || '/';
}

/** Заход на карточку из каталога/ленты/заказов и т.д. — тогда «назад» = история. Иначе (шаринг, прямой URL) — в каталог. */
function hasInAppProductNavigationState(state) {
  if (!state || typeof state !== 'object') return false;
  return Boolean(
    state.item ||
      state.fromRelated ||
      state.fromFeed ||
      state.fromOrders ||
      state.fromStartappItem
  );
}

function MainPageInner() {
  const { user } = useAuth();
  const browserBackHandlerRef = useBrowserBackHandlerRef();
  const mainContentRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('feed');
  const { setTabBarVisible } = useTabBarVisibility();

  useEffect(() => {
    const threshold = 80; // пикселей, считаем что клавиатура открыта если высота сильно уменьшилась
    const baseHeight = window.innerHeight;

    const handleResize = () => {
      const path = window.location.hash.replace('#', '') || '';
      const segments = path.split('/').filter(Boolean);
      const isRootTabPage =
        segments[0] === 'main' &&
        (segments.length === 1 || (segments.length === 2 && ROOT_TAB_SEGMENTS.includes(segments[1])));

      // На внутренних страницах (checkout, catalog/:id, profile/addresses и т.д.) таббар скрыт
      if (!isRootTabPage) {
        setTabBarVisible(false);
        return;
      }
      const currentHeight = window.innerHeight;
      const keyboardOpen = baseHeight - currentHeight > threshold;
      setTabBarVisible(!keyboardOpen);
    };

    window.addEventListener('resize', handleResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [setTabBarVisible]);

  const browserUiPath = useMemo(
    () => routePathForBrowserUi(location),
    [location.pathname, location.hash]
  );

  const isRootTabPage = useMemo(() => {
    const segments = browserUiPath.split('/').filter(Boolean);
    return (
      segments[0] === 'main' &&
      (segments.length === 1 || (segments.length === 2 && ROOT_TAB_SEGMENTS.includes(segments[1])))
    );
  }, [browserUiPath]);

  const isBrowserCatalogItemOrphan = useMemo(() => {
    if (isTelegramWebAppEnvironment()) return false;
    const segments = browserUiPath.split('/').filter(Boolean);
    const isLeafItem =
      segments.length === 3 &&
      segments[0] === 'main' &&
      segments[1] === 'catalog' &&
      /^\d+$/.test(segments[2]);
    return isLeafItem && !hasInAppProductNavigationState(location.state);
  }, [browserUiPath, location.state]);

  useEffect(() => {
    const tabSegment = browserUiPath.split('/').filter(Boolean)[1] || '';
    const rootTab = ['feed', 'cart', 'fitting', 'catalog', 'profile'].includes(tabSegment) ? tabSegment : null;
    track('screen_view', { path: browserUiPath, root_tab: rootTab });
  }, [browserUiPath]);

  useEffect(() => {
    const segments = browserUiPath.split('/').filter(Boolean);
    const tabSegment = segments[1]; // /main/<tab>

    // Определяем активную вкладку
    switch (tabSegment) {
      case 'cart':
        setActiveTab('cart');
        break;
      case 'fitting':
        setActiveTab('fitting');
        break;
      case 'catalog':
        setActiveTab('catalog');
        break;
      case 'profile':
        setActiveTab('profile');
        break;
      case 'feed':
      default:
        setActiveTab('feed');
        break;
    }

    // Таббар только на корневых вкладках (feed, cart, fitting, catalog, profile); на checkout и вложенных — скрыт
    setTabBarVisible(isRootTabPage);
  }, [browserUiPath, setTabBarVisible, isRootTabPage]);

  const showBrowserHeaderBack = !isTelegramWebAppEnvironment() && !isRootTabPage;
  const [catalogSharePayload, setCatalogSharePayload] = useState(null);

  const handleBrowserBack = useCallback(() => {
    const override = browserBackHandlerRef.current;
    if (typeof override === 'function') {
      override();
      return;
    }
    if (isBrowserCatalogItemOrphan) {
      navigate('/main/catalog');
      return;
    }
    navigate(-1);
  }, [browserBackHandlerRef, navigate, isBrowserCatalogItemOrphan]);

  const handleTabChange = (tabId) => {
    let segment = 'feed';
    if (tabId === 'cart') segment = 'cart';
    else if (tabId === 'fitting') segment = 'fitting';
    else if (tabId === 'catalog') segment = 'catalog';
    else if (tabId === 'profile') segment = 'profile';
    else if (tabId === 'feed') segment = 'feed';
    navigate(`/main/${segment}`, { replace: false });
  };

  const showBrowserCatalogShare =
    !isTelegramWebAppEnvironment() && catalogSharePayload != null;

  return (
    <div className="main-page">
      <header className="main-page__header" aria-hidden="false">
        <div className="main-page__header-leading">
          {showBrowserHeaderBack ? (
            <BrowserNavBackButton onClick={handleBrowserBack} />
          ) : (
            <span className="main-page__header-leading-spacer" aria-hidden />
          )}
        </div>
        <div className="main-page__header-center">
          <button
            type="button"
            className="main-page__logo-wrap"
            onClick={() => {
              const webGuest = !user && !hasTelegramWebAppInitData();
              navigate(webGuest ? '/main/catalog' : '/main/feed');
            }}
            aria-label={!user && !hasTelegramWebAppInitData() ? 'В каталог' : 'На ленту'}
          >
            <img src={logoUrl} alt="MatchWear" className="main-page__logo" />
          </button>
        </div>
        <div
          className="main-page__header-trailing"
          aria-hidden={!showBrowserCatalogShare}
        >
          {showBrowserCatalogShare ? (
            <BrowserCatalogShareButton
              itemId={catalogSharePayload.itemId}
              title={catalogSharePayload.title}
            />
          ) : (
            <span className="main-page__header-trailing-spacer" aria-hidden />
          )}
        </div>
      </header>
      <div className="main-page__content" ref={mainContentRef}>
        <CatalogProvider>
          <FeedProvider>
            <FittingDraftProvider>
              <CatalogShareDispatchContext.Provider value={setCatalogSharePayload}>
                <Outlet />
              </CatalogShareDispatchContext.Provider>
            </FittingDraftProvider>
          </FeedProvider>
        </CatalogProvider>
      </div>
      <TabBar activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

export default function MainPage() {
  return (
    <BrowserBackHandlerRefProvider>
      <MainPageInner />
    </BrowserBackHandlerRefProvider>
  );
}
